import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface NotifSetting {
  notification_type: string;
  category: string;
  label: string;
  description: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
}

interface ContactInfo {
  email: string;
  phone: string;
  threshold: number;
}

const CHANNEL_CONFIG = {
  email: { label: "이메일", icon: "ri-mail-line", color: "text-violet-400" },
  sms: { label: "SMS", icon: "ri-message-2-line", color: "text-pink-400" },
  push: { label: "푸시", icon: "ri-notification-3-line", color: "text-amber-400" },
};

export default function NotificationTab() {
  const { user, session } = useAuth();
  const [settings, setSettings] = useState<NotifSetting[]>([]);
  const [contact, setContact] = useState<ContactInfo>({ email: "", phone: "", threshold: 1000 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const fetchData = async () => {
    if (!user || !session) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "get", user_id: user.id }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSettings(data.settings || []);
      setContact({
        email: data.contact?.email || "",
        phone: data.contact?.phone || "",
        threshold: data.contact?.threshold ?? 1000,
      });
    } catch (err: any) {
      setError(err.message || "알림 설정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.id, session?.access_token]);

  const updateSetting = async (
    notification_type: string,
    updates: { email_enabled?: boolean; sms_enabled?: boolean; push_enabled?: boolean }
  ) => {
    if (!user || !session) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "update", user_id: user.id, notification_type, ...updates }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
    } catch (err: any) {
      showToast(err.message || "저장에 실패했습니다");
    }
  };

  const toggle = async (type: string, channel: "email_enabled" | "sms_enabled" | "push_enabled") => {
    setSettings((prev) =>
      prev.map((s) =>
        s.notification_type === type ? { ...s, [channel]: !s[channel] } : s
      )
    );
    const setting = settings.find((s) => s.notification_type === type);
    if (setting) {
      const updates = { [channel]: !setting[channel] };
      await updateSetting(type, updates as any);
    }
  };

  const toggleAll = async (channel: "email_enabled" | "sms_enabled" | "push_enabled", value: boolean) => {
    setSettings((prev) => prev.map((s) => ({ ...s, [channel]: value })));
    // Batch update each setting
    for (const s of settings) {
      if (s[channel] !== value) {
        await updateSetting(s.notification_type, { [channel]: value });
      }
    }
  };

  const handleSave = async () => {
    if (!user || !session) return;
    setSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "update_contact",
          user_id: user.id,
          email: contact.email,
          phone: contact.phone,
          threshold: Number(contact.threshold),
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      showToast("알림 설정이 저장되었습니다");
    } catch (err: any) {
      showToast(err.message || "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  const categories = [...new Set(settings.map((s) => s.category))];

  const allOn = (channel: "email_enabled" | "sms_enabled" | "push_enabled") =>
    settings.length > 0 && settings.every((s) => s[channel]);
  const allOff = (channel: "email_enabled" | "sms_enabled" | "push_enabled") =>
    settings.length > 0 && settings.every((s) => !s[channel]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-white font-bold text-base md:text-lg">알림 설정</h2>
        <p className="text-gray-500 text-xs md:text-sm mt-0.5">이메일, SMS, 푸시 알림 수신 여부를 설정합니다</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-red-400 hover:text-red-300 text-xs underline cursor-pointer">
            다시 시도
          </button>
        </div>
      )}

      {/* Notification channels */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {(["email_enabled", "sms_enabled", "push_enabled"] as const).map((ch) => {
          const key = ch.replace("_enabled", "") as "email" | "sms" | "push";
          const cfg = CHANNEL_CONFIG[key];
          const onCount = settings.filter((s) => s[ch]).length;
          return (
            <div key={ch} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05]">
                  <i className={`${cfg.icon} text-base ${cfg.color}`}></i>
                </div>
                <span className="text-white text-sm font-semibold">{cfg.label}</span>
              </div>
              <p className="text-gray-500 text-xs mb-3">{onCount}/{settings.length} 활성화</p>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleAll(ch, true)}
                  disabled={allOn(ch)}
                  className="flex-1 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-xs cursor-pointer whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  전체 켜기
                </button>
                <button
                  onClick={() => toggleAll(ch, false)}
                  disabled={allOff(ch)}
                  className="flex-1 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-xs cursor-pointer whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  전체 끄기
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Settings table */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-[1fr_80px_80px_80px] gap-4 px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
          <span className="text-gray-500 text-xs font-medium">알림 항목</span>
          {(["email_enabled", "sms_enabled", "push_enabled"] as const).map((ch) => {
            const key = ch.replace("_enabled", "") as "email" | "sms" | "push";
            return (
              <span key={ch} className="text-gray-500 text-xs font-medium text-center">{CHANNEL_CONFIG[key].label}</span>
            );
          })}
        </div>

        {categories.map((cat) => (
          <div key={cat}>
            <div className="px-5 py-2 bg-white/[0.02] border-b border-white/[0.04]">
              <span className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{cat}</span>
            </div>
            {settings.filter((s) => s.category === cat).map((setting, idx, arr) => (
              <div
                key={setting.notification_type}
                className={`flex flex-col md:grid md:grid-cols-[1fr_80px_80px_80px] gap-3 md:gap-4 px-4 md:px-5 py-4 items-start md:items-center transition-colors hover:bg-white/[0.02] ${
                  idx < arr.length - 1 ? "border-b border-white/[0.04]" : ""
                }`}
              >
                <div>
                  <p className="text-white text-sm font-medium">{setting.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{setting.description}</p>
                </div>

                {/* Mobile: channel labels + toggles in a row */}
                <div className="flex items-center gap-6 md:hidden w-full">
                  {(["email_enabled", "sms_enabled", "push_enabled"] as const).map((ch) => {
                    const key = ch.replace("_enabled", "") as "email" | "sms" | "push";
                    return (
                      <div key={ch} className="flex flex-col items-center gap-1">
                        <span className="text-gray-500 text-[10px]">{CHANNEL_CONFIG[key].label}</span>
                        <button
                          onClick={() => toggle(setting.notification_type, ch)}
                          className={`relative w-9 h-5 rounded-full transition-all cursor-pointer ${
                            setting[ch] ? "bg-gradient-to-r from-violet-600 to-pink-600" : "bg-white/[0.1]"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                              setting[ch] ? "left-4" : "left-0.5"
                            }`}
                          ></span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop toggles */}
                {(["email_enabled", "sms_enabled", "push_enabled"] as const).map((ch) => (
                  <div key={ch} className="hidden md:flex justify-center">
                    <button
                      onClick={() => toggle(setting.notification_type, ch)}
                      className={`relative w-10 h-5 rounded-full transition-all cursor-pointer ${
                        setting[ch] ? "bg-gradient-to-r from-violet-600 to-pink-600" : "bg-white/[0.1]"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                          setting[ch] ? "left-5" : "left-0.5"
                        }`}
                      ></span>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Contact info */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-contacts-line text-violet-400 text-base"></i>
          </div>
          알림 수신 정보
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">이메일 주소</label>
            <input
              type="email"
              value={contact.email}
              onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">SMS 수신 번호</label>
            <input
              type="text"
              value={contact.phone}
              onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-1.5">크레딧 부족 알림 기준</label>
            <div className="relative">
              <input
                type="number"
                value={contact.threshold}
                onChange={(e) => setContact((p) => ({ ...p, threshold: Number(e.target.value) }))}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 pr-10 text-white text-sm focus:outline-none focus:border-violet-500/50 transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">건</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 ${
            saved
              ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
              : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white hover:scale-105"
          }`}
        >
          {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
          <i className={`${saved ? "ri-checkbox-circle-line" : "ri-save-line"} text-base`}></i>
          {saved ? "저장됨" : "설정 저장"}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300">
          <i className="ri-checkbox-circle-line text-base"></i>
          <span className="text-sm font-medium">{toast}</span>
        </div>
      )}
    </div>
  );
}